<script lang="ts" setup>

import { ref, reactive } from 'vue';
import type { FormSubmitEvent } from '#ui/types'
import { z } from 'zod'
import { useRouter } from 'vue-router'

const { signIn } = useAuth();
const toast = useToast();
const router = useRouter();
const loading = ref(false);

const schema = z.object({
  email: z.string()
    .email('Email invalide')
    .refine((val) => !val.includes(' '), 'L\'email ne doit pas contenir d\'espaces')
    .or(z.literal('')),
  phone: z.string()
    .min(10, 'Numéro de téléphone invalide')
    .refine((val) => !val.includes('.') && !val.includes(' '), 'Le numéro de téléphone ne doit pas contenir de points ou d\'espaces')
    .or(z.literal('')),
  password: z.string().min(8, 'Mot de passe doit avoir au moins 8 caractères')
}).superRefine((data, ctx) => {
  if (!data.email && !data.phone) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Email ou numéro de téléphone est requis',
      path: ['email'],
    });
  }
});
type Schema = z.output<typeof schema>;

const form = reactive({
  email: '',
  phone: '',
  password: ''
});

async function onSubmit(event: FormSubmitEvent<Schema>) {
  loading.value = true;
  try {
    const result = await signIn('credentials', {
      email: form.email,
      phoneNumber: form.phone,
      password: form.password,
      redirect: false,
      callbackUrl: "/",
    });

    if (result?.error) {
      const errorMessage = result?.error === "Unauthorized"
          ? "Identifiants incorrects, veuillez réessayer."
          : `Une erreur est survenue : ${result?.error}`;

      toast.add({
        id: result?.error,
        icon: "i-heroicons-x-circle",
        color: "red",
        title: "Erreur de connexion",
        description: errorMessage,
      });
    } else {
      toast.add({
        id: "success",
        color: "green",
        icon: "i-heroicons-check-circle",
        title: `Bienvenue ${form.email || form.phone} !`,
        description: "Connexion réussie.",
      });
      await router.push("/admin/dashboard");
    }
  } catch (error: any) {
    toast.add({
      id: "error",
      icon: "i-heroicons-x-circle",
      color: "red",
      title: "Erreur inconnue" + error.status,
      description: error.message,
    });
  } finally {
    loading.value = false;
  }
}
</script>


<template>
  <div class="flex flex-1 h-screen min-h-full bg-white">
    <div class="flex flex-col justify-center flex-1 px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
      <div class="w-full max-w-sm mx-auto lg:w-96">

        <div class="mt-10">
          <div class="flex flex-col items-center">
            <UIcon name="i-heroicons-lock-closed" class="w-10 h-10 text-gray-600" />
            <h2 class="mt-2 text-2xl">Se connecter</h2>
            <p class="mt-1 text-sm text-gray-500">
              Pas encore de compte ? <a href="/register" class="text-greenCharlys-30 hover:text-greenCharlys-35 hover:underline">Inscrivez-vous</a>
            </p>
          </div>

          <UForm :schema="schema" :state="form" class="mt-4 space-y-4" @submit="onSubmit">
            <p class="text-sm text-center text-gray-600">
              Veuillez remplir <strong>soit votre adresse email</strong>, <strong>soit votre numéro de téléphone</strong>
            </p>
            <UFormGroup v-if="!form.phone" label="Email" name="email">
              <UInput v-model="form.email" />
            </UFormGroup>
            
            <UFormGroup v-if="!form.email" label="Téléphone" name="phone">
              <UInput v-model="form.phone" />
            </UFormGroup>

            <UFormGroup label="Mot de passe" name="password">
              <UInput v-model="form.password" type="password" />
            </UFormGroup>

            <div class="flex justify-center">
              <UButton type="submit" :loading="loading">
                Connexion
              </UButton>
            </div>
            <p class="text-sm text-center text-gray-500">
              Mot de passe oublié ? <a href="/auth/forgot-password" class="text-greenCharlys-30 hover:text-greenCharlys-35 hover:underline">Réinitialiser</a>
            </p>
          </UForm>
        </div>
      </div>
    </div>
    <div class="relative flex-1 hidden w-0 lg:block">
      <NuxtImg class="absolute inset-0 object-cover w-full h-full" src="/assets/nice01.webp" alt="" />
    </div>
  </div>
</template>
